/**
 * WEB-2054 — ЗАГРУЗЧИК кусков публичного рендерера блоков.
 *
 * Здесь три вещи, и все три нужны разным зовущим:
 *  - `preloadPublicBlockChunksFor` — ГРЕЛКА по составу воронки. Зовут страница
 *    показа и роутер хоста, как только тело показа на руках (тот же приём, что
 *    у грелки пейволла в `PublicHostRouter`, WEB-2015): куски едут параллельно
 *    ядру, а не после первого рендера.
 *  - `loadPublicBlockChunk` / `getLoadedPublicBlockChunk` — их читает дверь
 *    рендерера (`PublicBlockRendererGate`): кусок на руках — рисуем синхронно,
 *    нет — ждём под Suspense.
 *  - `registerPublicBlockChunk` — кусок положили СТАТИЧЕСКИ. Так делает кабинет
 *    (`registerPublicBlockChunksEagerly.ts`): в конструкторе превью обязано
 *    рисоваться тем же кадром, что и раньше, а лишний вес там не платит
 *    покупатель.
 *
 * Загрузка ОТРИСОВКИ идёт через `retryImport` — ту же обёртку, что у страницы
 * пейволла (WEB-1336): выкат новой сборки посреди сессии уносит хешированный
 * файл с CDN, и это обязано лечиться однократной перезагрузкой за свежим
 * `index.html`, а не вечным ожиданием на денежном пути. ГРЕЛКА перезагружать не
 * имеет права (починка ревизии, Р3) — она работает до и помимо желания
 * покупателя, и перезагрузка страницы у него на глазах хуже, чем блок, который
 * приедет чуть позже своим путём.
 *
 * Файл лежит ВНЕ `components/editor/`: публичный вход достаёт до него минуя
 * рендерер, а туда ход закрыт правилом `editor-bypassing-renderer` сторожа
 * сборки. Сами куски он тянет ДИНАМИЧЕСКИ — статический импорт любого из них
 * вернул бы их в первый экран, и это краснит `FIRST_SCREEN_DEFERRED_MODULES`.
 */
import type { ComponentType } from 'react';
import { retryImport } from '@/utils/lazyWithRetry';
import type { PublicExtendedBlockProps } from './publicExtendedBlockProps';
import {
  type PublicBlockChunkName,
  publicBlockChunksFor,
} from './publicBlockChunkMap';

export type PublicBlockChunkComponent = ComponentType<PublicExtendedBlockProps>;

type ChunkModule = { default: PublicBlockChunkComponent };

const IMPORTERS: Readonly<
  Record<PublicBlockChunkName, () => Promise<ChunkModule>>
> = {
  extended: () =>
    import('@/components/editor/PublicBlockRendererExtended').then(
      (module) => ({
        default: module.PublicBlockRendererExtended,
      }),
    ),
  portal: () =>
    import('@/components/editor/blocks/PortalBlocksRenderer').then(
      (module) => ({
        default: module.PortalBlocksRenderer,
      }),
    ),
  reference: () =>
    import('@/components/editor/blocks/ReferenceBlocksRenderer').then(
      (module) => ({ default: module.ReferenceBlocksRenderer }),
    ),
};

const loaded = new Map<PublicBlockChunkName, PublicBlockChunkComponent>();

/**
 * Начатый заказ куска. Кроме промиса помнит, ЧЕЙ он: заказ грелки права на
 * перезагрузку не несёт, и отрисовке, пришедшей поверх него, этого права не
 * хватает (см. `loadPublicBlockChunk`).
 */
interface ChunkRequest {
  promise: Promise<PublicBlockChunkComponent>;
  readonly reloadOnFailure: boolean;
}

const inFlight = new Map<PublicBlockChunkName, ChunkRequest>();

/**
 * ШОВ ДЛЯ ТЕСТОВОЙ ОБВЯЗКИ: синхронный добытчик куска.
 *
 * Сотни существующих тестов рисуют редкие виды блоков и читают их СИНХРОННО
 * (`getBy…`). Регистрировать все куски заранее (как это делает кабинет) значило
 * бы тянуть весь рендерер в КАЖДЫЙ файл тестов — плата за разрез, которую никто
 * не просил. Добытчик зовётся только когда куска нет: платят ровно те файлы,
 * которые редкий блок действительно рисуют.
 *
 * В сборке его никто не ставит: у публичного входа и у кабинета свои пути
 * (ленивый и статический соответственно).
 */
let syncResolver:
  ((name: PublicBlockChunkName) => PublicBlockChunkComponent | null) | null =
  null;

export function setPublicBlockChunkSyncResolver(
  resolver:
    ((name: PublicBlockChunkName) => PublicBlockChunkComponent | null) | null,
): void {
  syncResolver = resolver;
}

/** Кусок уже на руках — дверь нарисует его синхронно, без Suspense. */
export function getLoadedPublicBlockChunk(
  name: PublicBlockChunkName,
): PublicBlockChunkComponent | null {
  const known = loaded.get(name);
  if (known) return known;
  const resolved = syncResolver ? syncResolver(name) : null;
  if (resolved) loaded.set(name, resolved);
  return resolved ?? null;
}

/** Кусок положили статически (кабинет, тестовая обвязка). */
export function registerPublicBlockChunk(
  name: PublicBlockChunkName,
  component: PublicBlockChunkComponent,
): void {
  loaded.set(name, component);
}

/**
 * Загрузка куска. Промис один на кусок: повторный зов (грелка и следом
 * отрисовка) не заказывает второй файл.
 *
 * `reloadOnFailure` — ЧЕЙ это зов (починка ревизии, Р3).
 *  - ОТРИСОВКА (`true`, умолчание): `retryImport`, как у страницы пейволла
 *    (WEB-1336). Выкат посреди сессии уносит хешированный файл с CDN, и без
 *    однократной перезагрузки за свежим `index.html` блока не будет вовсе.
 *  - ГРЕЛКА (`false`): обычный `import()`. Греет она заранее и втихую, а
 *    перезагрузка — вещь, которую покупатель видит: старая оболочка (кеш
 *    посетителя или копия на краю до 60 с после выката) ссылается на снесённые
 *    файлы, и страница перезагружалась бы сама собой, ещё до единого нажатия.
 *
 * Отказ НЕ запоминается: неудачный промис вычёркивается, и следующий зов
 * (например, отрисовка после молчаливой неудачи грелки) идёт за файлом заново
 * — уже со своим правом на перезагрузку.
 *
 * ОТРИСОВКА ПОВЕРХ ЛЕТЯЩЕЙ ГРЕЛКИ (свежий взгляд пачки 35, С1). Раньше любой
 * начатый промис отдавался как есть, и «кто зовёт» учитывалось только у
 * ПЕРВОГО зовущего. А зовут в этом порядке почти всегда: грелка стартует до
 * первой отрисовки (страница показа зовёт её, ещё не положив тело показа в
 * состояние), значит дверь получала промис грелки — обычный `import()` без
 * права на перезагрузку. Выкат посреди сессии уносил файл куска, грелка
 * молчала по своему правилу, и лечения не было вовсе. Теперь отрисовка, не
 * найдя у начатого заказа своего права, ДОЖИДАЕТСЯ его и при отказе идёт за
 * файлом сама — через `retryImport`. Второй заход дешёв: успешный
 * динамический `import()` того же адреса браузер отдаёт из своего кэша, а
 * неудачный и есть тот случай, ради которого перезагрузка заведена. Защёлка
 * «уже перезагружали» живёт в `lazyWithRetry` и считается ПО ФАЙЛУ (ключ —
 * имя куска), поэтому перезагрузка остаётся однократной для этого куска и не
 * отнимает лечение у остальных.
 */
export function loadPublicBlockChunk(
  name: PublicBlockChunkName,
  reloadOnFailure = true,
): Promise<PublicBlockChunkComponent> {
  const started = inFlight.get(name);
  if (started) {
    if (!reloadOnFailure || started.reloadOnFailure) return started.promise;
    const upgraded: ChunkRequest = {
      promise: started.promise.catch(() => beginImport(name, true).promise),
      reloadOnFailure: true,
    };
    inFlight.set(name, upgraded);
    return upgraded.promise;
  }
  return beginImport(name, reloadOnFailure).promise;
}

/**
 * Заказать файл куска. Отказ вычёркивает ИМЕННО ЭТОТ заказ — сверка по
 * тождеству, а не по имени: пока промис летел, его мог сменить заказ
 * отрисовки, и вычёркивать чужой живой заказ нельзя (иначе следующая
 * отрисовка закажет файл третий раз).
 */
function beginImport(
  name: PublicBlockChunkName,
  reloadOnFailure: boolean,
): ChunkRequest {
  const imported = reloadOnFailure
    ? // Ключ защёлки — ИМЯ КУСКА: перезагрузка отмеряется каждому файлу своя
      // (свежий взгляд, Н1). Раньше защёлка была одна на визит, и первый же
      // не приехавший кусок тратил её на все остальные.
      retryImport(IMPORTERS[name], `chunk:${name}`)
    : IMPORTERS[name]();
  const request: ChunkRequest = { promise: null as never, reloadOnFailure };
  request.promise = imported.then(
    (module) => {
      loaded.set(name, module.default);
      return module.default;
    },
    (error: unknown) => {
      if (inFlight.get(name) === request) inFlight.delete(name);
      throw error;
    },
  );
  inFlight.set(name, request);
  return request;
}

/**
 * Грелка по составу воронки. Отказ проглатывается намеренно: тот же импорт
 * повторится при отрисовке под Suspense, и там его отказ покажет граница
 * падения двери — а необработанный промис из грелки уронил бы страницу на
 * ровном месте (так же молчит грелка пейволла в PublicHostRouter).
 *
 * Починка ревизии (Р3): грелка качает БЕЗ права на перезагрузку страницы —
 * второй параметр `loadPublicBlockChunk`. Раньше `.catch()` стоял поверх
 * `retryImport`, и он успевал перезагрузить страницу покупателя ДО того, как
 * отказ доходил до этого молчаливого обработчика.
 */
export function preloadPublicBlockChunksFor(types: Iterable<string>): void {
  for (const name of publicBlockChunksFor(types)) {
    void loadPublicBlockChunk(name, false).catch(() => {
      /* см. док выше */
    });
  }
}

/** Только для тестов: вернуть загрузчик в состояние «ничего не качали». */
export function __resetPublicBlockChunksForTests(): void {
  loaded.clear();
  inFlight.clear();
  syncResolver = null;
}
